import { PmNodeType } from "@storytell/types/richText";
import type { NodeViewProps } from "@tiptap/core";
import { For, type JSX } from "solid-js";
import { NodeViewWrapper, SolidNodeViewRenderer } from "tiptap-solid";

import { Node, mergeAttributes } from "@tiptap/core";
import { Dynamic } from "solid-js/web";

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export type VariableV1NodeProps<T = Record<string, any>> = NodeViewProps & {
  node: { attrs: { id: string; type: string; data: T | null } };
  // Special prop passed from the PromptAckRenderer to indicate that the variable is not in the prompt bar
  notInPromptBar?: boolean;
};

export type VariableV1Props = {
  renderer: (props: VariableV1NodeProps) => JSX.Element;
};

export const VariableV1 = (opts: VariableV1Props) =>
  Node.create({
    name: PmNodeType.VariableV1,
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
      return {
        type: "",
        id: "",
        text: "",
        data: null,
      };
    },

    renderText({ node }) {
      return `"${node.attrs.text}"`;
    },

    parseHTML() {
      return [
        {
          tag: "span[data-node='variable:v1']",
          getAttrs: (element: HTMLElement) => {
            let parsed = null;
            try {
              parsed = JSON.parse(element.getAttribute("data-data") || "null", (key, value) =>
                typeof value === "string" && value.startsWith("$bigint:") ? BigInt(value.slice(8)) : value,
              );
            } catch (e) {
              console.error(e);
            }

            return {
              type: element.getAttribute("data-type") || "",
              id: element.getAttribute("data-id") || "",
              text: element.getAttribute("data-text") || "",
              data: parsed,
            };
          },
        },
      ];
    },

    renderHTML({ node, HTMLAttributes }) {
      return [
        "span",
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
          "data-node": "variable:v1",
          "data-id": node.attrs.id,
          "data-type": node.attrs.type,
          "data-text": node.attrs.text,
          "data-data": JSON.stringify(node.attrs.data, (key, value) =>
            typeof value === "bigint" ? `$bigint:${value.toString()}` : value,
          ),
        }),
      ];
    },

    addNodeView() {
      return SolidNodeViewRenderer((props: VariableV1NodeProps) => (
        <NodeViewWrapper as={"span"}>
          <Dynamic component={opts.renderer} {...props} />
        </NodeViewWrapper>
      ));

      // return SolidNodeViewRenderer(
      //   (
      //     props: VariableV1NodeProps,
      //   ) => {
      //     const data = [
      //       { year: 2018, revenueGrowth: 4.2 },
      //       { year: 2019, revenueGrowth: 5.7 },
      //       { year: 2020, revenueGrowth: 3.9 },
      //       { year: 2021, revenueGrowth: 7.9 },
      //       { year: 2022, revenueGrowth: 9.2 },
      //       { year: 2023, revenueGrowth: 10.5 },
      //     ];

      //     // Chart dimensions
      //     const width = 600;
      //     const height = 400;
      //     const margin = { top: 40, right: 40, bottom: 60, left: 80 };
      //     const chartWidth = width - margin.left - margin.right;
      //     const chartHeight = height - margin.top - margin.bottom;

      //     // Calculate scales
      //     const maxRevenue = Math.max(...data.map((d) => d.revenueGrowth));
      //     const barWidth = (chartWidth / data.length) * 0.8;
      //     const barSpacing = (chartWidth / data.length) * 0.2;

      //     return (
      //       <NodeViewWrapper>
      //         <div class="flex justify-center py-4">
      //           <svg
      //             width={width}
      //             height={height}
      //             class="border border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-dark-background"
      //           >
      //             {/* Y-axis */}
      //             <line
      //               x1={margin.left}
      //               y1={margin.top}
      //               x2={margin.left}
      //               y2={height - margin.bottom}
      //               class="stroke-zinc-700 dark:stroke-zinc-300"
      //               stroke-width="2"
      //             />

      //             {/* X-axis */}
      //             <line
      //               x1={margin.left}
      //               y1={height - margin.bottom}
      //               x2={width - margin.right}
      //               y2={height - margin.bottom}
      //               class="stroke-zinc-700 dark:stroke-zinc-300"
      //               stroke-width="2"
      //             />

      //             {/* Y-axis label */}
      //             <text
      //               x={margin.left - 40}
      //               y={height / 2}
      //               transform={`rotate(-90 ${margin.left - 40} ${height / 2})`}
      //               text-anchor="middle"
      //               class="text-sm font-medium fill-zinc-700 dark:fill-zinc-300"
      //             >
      //               Revenue Growth %
      //             </text>

      //             {/* X-axis label */}
      //             <text
      //               x={width / 2}
      //               y={height - 10}
      //               text-anchor="middle"
      //               class="text-sm font-medium fill-zinc-700 dark:fill-zinc-300"
      //             >
      //               Years
      //             </text>

      //             {/* Y-axis ticks and grid lines */}
      //             <For each={[0, 2, 4, 6, 8, 10, 12]}>
      //               {(tick) => {
      //                 const y = height - margin.bottom - (tick / 12) * chartHeight;
      //                 return (
      //                   <>
      //                     <line
      //                       x1={margin.left}
      //                       y1={y}
      //                       x2={width - margin.right}
      //                       y2={y}
      //                       class="stroke-zinc-200 dark:stroke-zinc-600"
      //                       stroke-width="1"
      //                     />
      //                     <text
      //                       x={margin.left - 10}
      //                       y={y + 4}
      //                       text-anchor="end"
      //                       class="text-xs fill-zinc-500 dark:fill-zinc-400"
      //                     >
      //                       {tick}%
      //                     </text>
      //                   </>
      //                 );
      //               }}
      //             </For>

      //             {/* Bars */}
      //             <For each={data}>
      //               {(item, index) => {
      //                 const x = margin.left + index() * (chartWidth / data.length) + barSpacing / 2;
      //                 const barHeight = (item.revenueGrowth / 12) * chartHeight;
      //                 const y = height - margin.bottom - barHeight;

      //                 return (
      //                   <>
      //                     <rect
      //                       x={x}
      //                       y={y}
      //                       width={barWidth}
      //                       height={barHeight}
      //                       class="fill-blue-500 hover:fill-blue-600 dark:fill-blue-400 dark:hover:fill-blue-300 transition-colors"
      //                     />
      //                     <text
      //                       x={x + barWidth / 2}
      //                       y={y - 5}
      //                       text-anchor="middle"
      //                       class="text-xs font-medium fill-gray-700 dark:fill-gray-300"
      //                     >
      //                       {item.revenueGrowth}%
      //                     </text>
      //                     <text
      //                       x={x + barWidth / 2}
      //                       y={height - margin.bottom + 20}
      //                       text-anchor="middle"
      //                       class="text-xs fill-gray-500 dark:fill-gray-400"
      //                     >
      //                       {item.year}
      //                     </text>
      //                   </>
      //                 );
      //               }}
      //             </For>
      //           </svg>
      //         </div>
      //       </NodeViewWrapper>
      //     );
      //   },
      // );
    },
  });
